<script lang="ts">
    import { AskPositionMessage_AskType } from "@workadventure/messages";
    import highlightWords from "highlight-words";
    import { defaultColor, defaultWoka } from "@workadventure/shared-utils";
    import { localUserStore } from "../../../Connection/LocalUserStore";
    import { availabilityStatusStore } from "../../../Stores/MediaStore";
    import { getColorHexOfStatus } from "../../../Utils/AvailabilityStatus";
    import type { ChatUser } from "../../Connection/ChatConnection";
    import { LL } from "../../../../i18n/i18n-svelte";
    import { chatSearchBarValue } from "../../Stores/ChatStore";
    import { gameManager } from "../../../Phaser/Game/GameManager";
    import { analyticsClient } from "../../../Administration/AnalyticsClient";
    import { getColorByString } from "../../../Utils/ColorGenerator";
    import { userListStore } from "../../../Stores/UserListStore";
    import { triggerClickOnEnterOrSpace } from "../../../Utils/svelte-a11y";
    import UserActionButton from "./UserActionButton.svelte";
    import ImageWithFallback from "./ImageWithFallback.svelte";
    import { IconLoader } from "@wa-icons";

    interface Props {
        user: ChatUser;
        isMatrixChatEnabled: boolean;
    }

    let { user, isMatrixChatEnabled = true }: Props = $props();

    let { availabilityStatus, username = "", isAdmin, pictureStore } = $derived(user);

    let userColor = $derived(getColorByString(username));

    let isMe = $derived(
        user.chatId === localUserStore.getChatId() || user.uuid === localUserStore.getLocalUser()?.uuid,
    );

    let userStatus = $derived(isMe ? availabilityStatusStore : availabilityStatus);

    let chunks = $derived(
        highlightWords({
            text: username.match(/\[\d*]/) ? username.substring(0, username.search(/\[\d*]/)) : username,
            query: $chatSearchBarValue,
        }),
    );

    let loadingDirectRoomAccess = false;

    function openWokaMenu() {
        if (user.uuid == undefined) return;
        // Track the open woka menu action
        analyticsClient.trackAdminEvent("user.woka_menu.opened");

        const currentScene = gameManager.getCurrentGameScene();

        const remotePlayerData = currentScene.getRemotePlayersRepository().getPlayerByUuid(user.uuid);
        if (remotePlayerData != undefined) {
            const remotePlayer = currentScene.MapPlayersByKey.get(remotePlayerData.userId);
            if (remotePlayer != undefined) {
                remotePlayer.activate();
                userListStore.toggle();
                return;
            }
        }

        currentScene.connection?.emitAskPosition(
            user.uuid ?? "",
            user.playUri ?? "",
            AskPositionMessage_AskType.LOCATE,
        );
        userListStore.toggle();
    }
</script>

{#if loadingDirectRoomAccess}
    <div class="min-h-[60px] text-md flex gap-2 justify-center flex-row items-center p-1">
        <IconLoader class="animate-spin" />
    </div>
{:else}
    <div class="flex flex-col user text-mdark">
        <div
            class="wa-chat-item {isAdmin ? 'admin' : 'user'} group/chatItem relative mb-px text-md flex gap-1
            flex-col items-center hover:bg-white/10 transition-all hover:rounded hover:cursor-pointer!
            cursor-pointer"
        >
            <div
                class="relative wa-avatar {!$userStatus ? 'opacity-50' : ''} cursor-pointer w-9 h-9 rounded-full"
                style={`background-color: ${userColor ?? defaultColor}`}
                onclick={(event) => {
                    event.stopPropagation();
                    openWokaMenu();
                }}
                onkeydown={triggerClickOnEnterOrSpace}
                role="button"
                tabindex="0"
                aria-label="Открыть меню пользователя"
            >
                <div class="w-9 h-9 rounded-full overflow-hidden">
                    <div class="translate-y-[3px] translate-x-[-3px] group-hover/chatItem:translate-y-0 transition-all">
                        <ImageWithFallback
                            classes="w-10 h-10 max-w-none"
                            src={$pictureStore}
                            alt={username}
                            fallback={defaultWoka}
                        />
                    </div>
                </div>
            </div>
            <div
                class={`flex-auto ${!$userStatus && "opacity-50"} cursor-pointer`}
                onclick={(event) => {
                    event.stopPropagation();
                    openWokaMenu();
                }}
                onkeydown={triggerClickOnEnterOrSpace}
                role="button"
                tabindex="0"
            >
                <div class="flex items-center h-4">
                    <div class="text-sm font-bold mb-0 flex items-center text-nowrap">
                        {#if isMe}
                            <span class="font-normal text-xs" data-testid="user-list-you">{$LL.chat.you()}</span>
                        {:else if $userStatus}
                            <div
                                class="flex items-center brightness-150"
                                style="color:{getColorHexOfStatus($userStatus)}"
                            >
                                {#if $userStatus}
                                    <div
                                        class="rounded-full me-1 h-1.5 w-1.5"
                                        style="background:{getColorHexOfStatus($userStatus)}"
                                    ></div>
                                {/if}
                            </div>
                        {/if}
                        {#if !isMe}
                            {#each chunks as chunk (chunk.key)}
                                <div class={`${chunk.match ? "text-light-blue" : ""}`}>
                                    {chunk.text}
                                </div>
                            {/each}
                        {/if}
                        {#if username && username.match(/\[\d*]/)}
                            <div class="font-light text-xs text-gray">
                                #{username
                                    .match(/\[\d*]/)
                                    ?.join()
                                    ?.replace("[", "")
                                    ?.replace("]", "")}
                            </div>
                        {/if}
                        {#if isAdmin}
                            <div
                                class="text-xxs bg-mblue rounded-sm px-1 py-0.5 ms-1 text-mlight"
                                title={$LL.chat.role.admin()}
                            >
                                {$LL.chat.role.adminShort()}
                            </div>
                        {/if}
                    </div>
                </div>
            </div>
            <div class="flex flex-wrap justify-center items-start content-start">
                <div class="transition-all">
                    {#if !isMe && $userStatus != 0}
                        <UserActionButton {user} {isMatrixChatEnabled} />
                    {/if}
                </div>
            </div>
        </div>
    </div>
{/if}
